import { generateClient } from "aws-amplify/api";
import { generatePresignedUrlForPut } from '@/graphql/mutations'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'
import { parseAwsJson } from '@/utils/graphql'
import sessionGraphqlMixin from '@/mixins/Organization/sessionGraphqlMixin'
import { normalizeS3Prefix } from '@/utils/s3Storage'
import {
  resolveKnowledgeBaseS3Prefix,
  uploadKnowledgeBaseS3Object,
  buildKnowledgeBaseMetadataDocument,
  buildKnowledgeBaseObjectMetadata,
} from '@/utils/knowledgeBaseS3'

const client = generateClient();

export const KNOWLEDGE_BASE_MIN_LENGTH = 250;

export function getUploadFileFromValue(value) {
  if (!value) return null;
  if (value instanceof File || value instanceof Blob) return value;

  if (Array.isArray(value)) {
    for (const item of value) {
      const file = getUploadFileFromValue(item);
      if (file) return file;
    }
    return null;
  }

  if (typeof value === "object") {
    if (value.file instanceof File || value.file instanceof Blob) return value.file;
    if (value.raw instanceof File || value.raw instanceof Blob) return value.raw;
  }

  return null;
}

export function normalizeKnowledgeBaseFile(value) {
  return getUploadFileFromValue(value);
}

export function normalizeKnowledgeBaseFiles(value) {
  if (!value) return [];

  if (value instanceof File || value instanceof Blob) {
    return [value];
  }

  if (Array.isArray(value)) {
    const files = [];
    for (const item of value) {
      files.push(...normalizeKnowledgeBaseFiles(item));
    }
    return files;
  }

  const file = getUploadFileFromValue(value);
  return file ? [file] : [];
}

function sanitizeKnowledgeBaseFileStem(name) {
  return (
    String(name || "file")
      .replace(/\.[^.]+$/, "")
      .replace(/[^a-zA-Z0-9 _-]/g, "_")
      .trim()
      .slice(0, 40) || "file"
  );
}

export const KNOWLEDGE_BASE_SEQUENTIAL_STEM = "Knowledge base";

const KNOWLEDGE_BASE_SEQUENTIAL_PATTERN = /^knowledge base-(\d+)/i;

export function nextKnowledgeBaseSequence(labels = []) {
  let max = 0;
  for (const label of labels) {
    const match = KNOWLEDGE_BASE_SEQUENTIAL_PATTERN.exec(
      String(label || "").trim(),
    );
    if (match) max = Math.max(max, Number(match[1]));
  }
  return max + 1;
}

/**
 * Rename newly uploaded files to sequential "Knowledge base-N" labels.
 * Files listed in skipNames (replacements of existing files) keep their
 * name so the replace-by-label flow still matches.
 */
export function assignSequentialKnowledgeBaseNames(
  files = [],
  existingLabels = [],
  skipNames = new Set(),
) {
  let next = nextKnowledgeBaseSequence(existingLabels);

  return files.map((file) => {
    const name = String(file?.name || "").trim();
    if (skipNames.has(name.toLowerCase())) return file;

    const ext = getKnowledgeBaseExtension(file);
    if (!ext) return file;

    const renamed = new File(
      [file],
      `${KNOWLEDGE_BASE_SEQUENTIAL_STEM}-${next}.${ext}`,
      {
        type: file.type || getKnowledgeBaseMimeType(ext),
        lastModified: file.lastModified,
      },
    );
    next += 1;
    return renamed;
  });
}

export function getKnowledgeBaseExtension(file) {
  const normalized = normalizeKnowledgeBaseFile(file);
  const name = normalized?.name?.toLowerCase() || "";
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".txt")) return "txt";
  return null;
}

export function getKnowledgeBaseMimeType(ext) {
  return ext === "pdf" ? "application/pdf" : "text/plain";
}

export function buildKnowledgeBaseS3Key(orgId, file, index = 0, kbS3Prefix = '', options = {}) {
  const ext = getKnowledgeBaseExtension(file);
  const stem = sanitizeKnowledgeBaseFileStem(file?.name);
  const prefix =
    normalizeS3Prefix(kbS3Prefix) ||
    resolveKnowledgeBaseS3Prefix({ org_id: orgId });
  const uniqueSuffix = options.unique ? `_${Date.now()}` : '';
  return `${prefix}${orgId}-${stem}${uniqueSuffix}_overview.${ext}`;
}

export function buildKnowledgeBaseMetadataS3Key(orgId, file, index = 0, kbS3Prefix = '', options = {}) {
  return `${buildKnowledgeBaseS3Key(orgId, file, index, kbS3Prefix, options)}.metadata.json`;
}

/** @deprecated Use buildKnowledgeBaseS3Key */
export function buildOverviewS3Key(orgId, ext) {
  return `organizations/${orgId}/${orgId}_overview.${ext}`;
}

/** @deprecated Use buildKnowledgeBaseMetadataS3Key */
export function buildMetadataS3Key(orgId, ext) {
  return `organizations/${orgId}/${orgId}_overview.${ext}.metadata.json`;
}

/** Whether the presigned URL requires a Content-Type header on PUT. */
export function presignedUrlRequiresContentType(presignedUrl) {
  try {
    const signedHeaders =
      new URL(presignedUrl).searchParams.get("X-Amz-SignedHeaders") || "";
    return signedHeaders
      .toLowerCase()
      .split(";")
      .map((h) => h.trim())
      .includes("content-type");
  } catch {
    return false;
  }
}

export function buildPresignedUploadHeaders(presignedUrl, contentType) {
  if (!contentType || !presignedUrlRequiresContentType(presignedUrl)) {
    return {};
  }
  return { "Content-Type": contentType };
}

export default {
  mixins: [sessionGraphqlMixin],
  methods: {
    async getPresignedUrlForPut(s3Key, fileType) {
      try {
        const result = await client.graphql({
          query: generatePresignedUrlForPut,
          variables: {
            input: {
              s3_key: s3Key,
              file_type: fileType,
            },
          },
        })
        return parseAwsJson(result.data?.generatePresignedUrlForPut)
      } catch (error) {
        console.log("Err", error);
        return {
          status: "ERROR",
          message: getApiMessage(error, "Failed to get presigned URL"),
        };
      }
    },

    resolvePresignedUrl(response) {
      if (!response) return null;

      const candidates = [
        response.presigned_url,
        response.presignedUrl,
        response.url,
        response.upload_url,
        response.uploadUrl,
        response.data?.presigned_url,
        response.data?.presignedUrl,
        response.data?.url,
        response.data?.upload_url,
        response.data?.uploadUrl,
      ];

      const match = candidates.find((value) => typeof value === "string" && value.trim());
      return match?.trim() || null;
    },

    /**
     * PUT to S3 presigned URL.
     * Used by org logo uploads; knowledge base uploads use Amplify uploadData.
     */
    async httpsUploadMethod(presignedUrl, payload, contentType) {
      const headers = buildPresignedUploadHeaders(presignedUrl, contentType);

      let body = payload;
      if (payload instanceof Blob || payload instanceof File) {
        if (headers["Content-Type"]) {
          body = payload;
        } else {
          body = await payload.arrayBuffer();
        }
      }

      try {
        const response = await fetch(presignedUrl, {
          method: "PUT",
          body,
          headers,
        });

        if (response.ok) {
          return { ok: true };
        }

        const responseText = await response.text().catch(() => "");
        return {
          ok: false,
          message:
            responseText ||
            response.statusText ||
            `Upload failed (${response.status})`,
          status: response.status,
        };
      } catch (error) {
        console.log("Err", error);
        const isNetworkCors =
          error?.name === "TypeError" &&
          /failed to fetch|networkerror/i.test(error?.message || "");

        return {
          ok: false,
          message: isNetworkCors
            ? "Upload blocked (CORS). Ensure the S3 bucket allows PUT from this app origin."
            : getApiMessage(error, "Upload failed"),
        };
      }
    },

    async uploadKnowledgeBaseFile(org, fileInput, index = 0, kbS3Prefix = '', options = {}) {
      const file = normalizeKnowledgeBaseFile(fileInput);
      if (!file) {
        return {
          status: "ERROR",
          message: "Please select a file",
        };
      }

      const orgId = String(org?.org_id ?? "").trim();
      if (!orgId) {
        return {
          status: "ERROR",
          message: "Organisation details are unavailable.",
        };
      }

      const ext = getKnowledgeBaseExtension(file);
      if (!ext) {
        return {
          status: "ERROR",
          message: "Only .txt and .pdf files are allowed",
        };
      }

      const contentType = getKnowledgeBaseMimeType(ext);
      const overviewKey = buildKnowledgeBaseS3Key(orgId, file, index, kbS3Prefix, {
        unique: Boolean(options.unique),
      });
      const metadataKey = `${overviewKey}.metadata.json`;
      const objectMetadata = buildKnowledgeBaseObjectMetadata(org);
      const metadataDocument = buildKnowledgeBaseMetadataDocument(org);

      try {
        await uploadKnowledgeBaseS3Object({
          path: overviewKey,
          data: file,
          contentType,
          metadata: objectMetadata,
        });

        await uploadKnowledgeBaseS3Object({
          path: metadataKey,
          data: JSON.stringify(metadataDocument),
          contentType: 'application/json',
        });
      } catch (error) {
        console.error('Knowledge base Amplify upload failed', error);
        return {
          status: "ERROR",
          message: getApiMessage(error, "Failed to upload knowledge base file"),
        };
      }

      return {
        status: "SUCCESS",
        s3_key: overviewKey,
        message: "Knowledge base file uploaded",
      };
    },

    async uploadKnowledgeBaseFiles(org, fileInput, kbS3Prefix = '', uniqueFileNames = []) {
      const files = normalizeKnowledgeBaseFiles(fileInput);
      if (!files.length) {
        return {
          status: "ERROR",
          message: "Please select at least one file",
        };
      }

      const uniqueNames = new Set(
        (Array.isArray(uniqueFileNames) ? uniqueFileNames : []).map((name) =>
          String(name || "").trim(),
        ).filter(Boolean),
      );

      const s3_keys = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const uploadResult = await this.uploadKnowledgeBaseFile(
          org,
          file,
          index,
          kbS3Prefix,
          { unique: uniqueNames.has(file?.name) },
        );
        if (uploadResult?.status !== "SUCCESS") {
          return uploadResult;
        }
        s3_keys.push(uploadResult.s3_key);
      }

      return {
        status: "SUCCESS",
        s3_keys,
        s3_key: s3_keys[0],
        message: "Knowledge base files uploaded",
      };
    },
  },
};
